<template>
    <section class="flag-guide">
        <button type="button" class="flag-guide__header" :aria-expanded="isOpen" aria-controls="flag-guide-content"
            @click="toggleGuide">
            <span class="flag-guide__heading">
                <span class="flag-guide__icon" aria-hidden="true">i</span>

                <span class="flag-guide__heading-text">
                    <span class="flag-guide__title">
                        Flag / Attribute Guide
                    </span>

                    <span class="flag-guide__subtitle">
                        Meaning of the attendance codes used in this report
                    </span>
                </span>
            </span>

            <span class="flag-guide__action">
                {{ isOpen ? "Hide" : "Show" }}

                <span class="flag-guide__chevron" :class="{ 'flag-guide__chevron--open': isOpen }" aria-hidden="true" />
            </span>
        </button>

        <transition name="flag-guide-slide">
            <div v-show="isOpen" id="flag-guide-content" class="flag-guide__content">
                <div v-for="flag in flags" :key="flag.code" class="flag-guide__item">
                    <span class="flag-guide__code">
                        {{ flag.code }}
                    </span>

                    <span class="flag-guide__meaning">
                        {{ flag.meaning }}
                    </span>
                </div>
            </div>
        </transition>
    </section>
</template>

<script>
export default {
    name: "FlagAttributeGuide",

    props: {
        flagDefinitions: {
            type: Array,
            default: () => []
        },

        openByDefault: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            isOpen: this.openByDefault,

            defaultFlags: [
                {
                    code: "A",
                    meaning: "ABSENT"
                },
                {
                    code: "A-RA",
                    meaning: "ABSENT - ROSTER ABSENT"
                },
                {
                    code: "A-MAP",
                    meaning: "ABSENT - MINIMUM ATTENDANCE POLICY"
                },
                {
                    code: "A-NJ",
                    meaning: "ABSENT - NOT JOINED"
                },
                {
                    code: "EO",
                    meaning: "EARLY OUT"
                },
                {
                    code: "EO + PL",
                    meaning: "EARLY OUT + PAID LEAVE"
                },
                {
                    code: "LA",
                    meaning: "LATE ARRIVAL"
                },
                {
                    code: "LA + PL",
                    meaning: "LATE ARRIVAL + PAID LEAVE"
                },
                {
                    code: "OFF - RD",
                    meaning: "OFF - REST DAY"
                },
                {
                    code: "P - FD",
                    meaning: "PRESENT - FULL DAY"
                },
                {
                    code: "UHD",
                    meaning: "UNPAID HALF DAY"
                },
                {
                    code: "Res. / Term. ",
                    meaning: "RESIGN/TERMINATE"
                },
                {
                    code: "P - (PL + ATT.)",
                    meaning: "PRESENT - (PAID LEAVE + ATTENDANCE)"
                },

            ]
        };
    },

    computed: {
        flags() {
            return this.flagDefinitions.length
                ? this.flagDefinitions
                : this.defaultFlags;
        }
    },

    methods: {
        toggleGuide() {
            this.isOpen = !this.isOpen;
        },

        openGuide() {
            this.isOpen = true;
        },

        closeGuide() {
            this.isOpen = false;
        }
    }
};
</script>

<style scoped>
.flag-guide {
    --flag-primary: #b2252d;
    --flag-primary-dark: #941c24;
    --flag-primary-light: #fff3f4;
    --flag-text: #202832;
    --flag-muted: #68727d;
    --flag-border: #dfe3e7;

    width: 100%;
    margin: 0 0 18px;
    overflow: hidden;
    border: 1px solid var(--flag-border);
    border-left: 5px solid var(--flag-primary);
    border-radius: 12px;
    background: #ffffff;
    box-shadow: 0 3px 12px rgba(28, 35, 43, 0.06);
}

.flag-guide__header {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 16px 20px;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;
}

.flag-guide__header:hover {
    background: #fffafa;
}

.flag-guide__header:focus-visible {
    outline: 3px solid rgba(178, 37, 45, 0.2);
    outline-offset: -3px;
}

.flag-guide__heading {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.flag-guide__icon {
    display: inline-flex;
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    border: 1px solid #e6b8bc;
    border-radius: 50%;
    background: var(--flag-primary-light);
    color: var(--flag-primary);
    font-size: 15px;
    font-weight: 700;
}

.flag-guide__heading-text {
    min-width: 0;
}

.flag-guide__title {
    display: block;
    color: var(--flag-text);
    font-size: 16px;
    font-weight: 700;
}

.flag-guide__subtitle {
    display: block;
    margin-top: 3px;
    color: var(--flag-muted);
    font-size: 13px;
    font-weight: 400;
}

.flag-guide__action {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 10px;
    color: var(--flag-primary);
    font-size: 13px;
    font-weight: 600;
}

.flag-guide__chevron {
    width: 8px;
    height: 8px;
    border-right: 2px solid var(--flag-primary);
    border-bottom: 2px solid var(--flag-primary);
    transform: rotate(45deg) translateY(-2px);
    transition: transform 0.2s ease;
}

.flag-guide__chevron--open {
    transform: rotate(225deg) translate(-1px, -1px);
}

.flag-guide__content {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 40px;
    row-gap: 12px;
    padding: 4px 20px 20px 60px;
}

.flag-guide__item {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 12px;
}

.flag-guide__code {
    display: inline-flex;
    flex: 0 0 82px;
    min-height: 30px;
    align-items: center;
    justify-content: center;
    padding: 4px 10px;
    border: 1px solid #e7b9bd;
    border-radius: 7px;
    background: var(--flag-primary-light);
    color: var(--flag-primary-dark);
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
}

.flag-guide__meaning {
    color: var(--flag-text);
    font-size: 13px;
    line-height: 1.45;
}

.flag-guide-slide-enter-active,
.flag-guide-slide-leave-active {
    max-height: 300px;
    overflow: hidden;
    opacity: 1;
    transition:
        max-height 0.25s ease,
        opacity 0.2s ease;
}

.flag-guide-slide-enter-from,
.flag-guide-slide-leave-to {
    max-height: 0;
    opacity: 0;
}

@media (max-width: 768px) {
    .flag-guide__header {
        padding: 14px 16px;
    }

    .flag-guide__content {
        grid-template-columns: 1fr;
        padding: 4px 16px 18px;
    }

    .flag-guide__item {
        align-items: flex-start;
    }

    .flag-guide__code {
        flex-basis: 78px;
    }
}

@media (max-width: 480px) {
    .flag-guide__subtitle {
        display: none;
    }

    .flag-guide__title {
        font-size: 14px;
    }

    .flag-guide__action {
        font-size: 0;
    }
}
</style>